import { SearchType } from 'types/loctions';

export type ValidationFilterItem = {
  key: string;
  value: string;
  label: string;
  level?: number;
};

const KEYS = {
  page: 'av_page',
  keyword: 'av_kw',
  sort: 'av_sort',
  order: 'av_ord',
  dateStart: 'av_ds',
  dateEnd: 'av_de',
  criticality: 'av_crit',
  maintType: 'av_mt',
  maintDate: 'av_md',
  searchType: 'av_st',
  filters: 'av_f',
  /** Submitted asset id for expanded row (detail panel open). */
  expand: 'av_expand'
} as const;

export type AssetValidationUrlSlice = {
  page: number;
  keyword: string;
  sort: string;
  order: 'asc' | 'desc' | undefined;
  dateStart: string;
  dateEnd: string;
  criticality: string;
  maintType: string;
  maintDate: string | undefined;
  searchType: SearchType;
  filters: ValidationFilterItem[] | undefined;
  expandedSubmittedAssetId: number | undefined;
};

const DEFAULTS: AssetValidationUrlSlice = {
  page: 1,
  keyword: '',
  sort: '',
  order: undefined,
  dateStart: '',
  dateEnd: '',
  criticality: '0',
  maintType: 'All',
  maintDate: undefined,
  searchType: 'partial-text',
  filters: undefined,
  expandedSubmittedAssetId: undefined
};

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw == null || raw === '') return fallback;
  const n = parseInt(raw, 10);
  return !Number.isNaN(n) && n > 0 ? n : fallback;
}

function parseOrder(raw: string | null): 'asc' | 'desc' | undefined {
  if (raw === 'asc' || raw === 'desc') return raw;
  return undefined;
}

function parseSearchType(raw: string | null): SearchType {
  if (raw === 'full-text' || raw === 'partial-text') return raw;
  return DEFAULTS.searchType;
}

function parseFilters(raw: string | null): ValidationFilterItem[] | undefined {
  if (raw == null || raw === '') return undefined;
  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded) as unknown;
    if (!Array.isArray(parsed)) return undefined;
    return parsed as ValidationFilterItem[];
  } catch {
    return undefined;
  }
}

function parseExpandAssetId(raw: string | null): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = parseInt(raw, 10);
  return !Number.isNaN(n) && n > 0 ? n : undefined;
}

/** Read Asset Validation slice from the current query string (av_* keys). */
export function parseAssetValidationParams(searchParams: URLSearchParams): AssetValidationUrlSlice {
  return {
    page: parsePositiveInt(searchParams.get(KEYS.page), DEFAULTS.page),
    keyword: searchParams.get(KEYS.keyword) ?? DEFAULTS.keyword,
    sort: searchParams.get(KEYS.sort) ?? DEFAULTS.sort,
    order: parseOrder(searchParams.get(KEYS.order)),
    dateStart: searchParams.get(KEYS.dateStart) ?? DEFAULTS.dateStart,
    dateEnd: searchParams.get(KEYS.dateEnd) ?? DEFAULTS.dateEnd,
    criticality: searchParams.get(KEYS.criticality) ?? DEFAULTS.criticality,
    maintType: searchParams.get(KEYS.maintType) ?? DEFAULTS.maintType,
    maintDate: searchParams.get(KEYS.maintDate) || undefined,
    searchType: parseSearchType(searchParams.get(KEYS.searchType)),
    filters: parseFilters(searchParams.get(KEYS.filters)),
    expandedSubmittedAssetId: parseExpandAssetId(searchParams.get(KEYS.expand))
  };
}

function deleteAvKeys(params: URLSearchParams): void {
  const toRemove = [...params.keys()].filter((k) => k.startsWith('av_'));
  toRemove.forEach((k) => params.delete(k));
}

/** Merge Asset Validation state into search params; preserves tab and non-av keys. */
export function mergeAssetValidationUrl(
  base: URLSearchParams,
  slice: AssetValidationUrlSlice
): URLSearchParams {
  const next = new URLSearchParams(base);
  deleteAvKeys(next);

  if (slice.page !== DEFAULTS.page) next.set(KEYS.page, String(slice.page));
  if (slice.keyword !== DEFAULTS.keyword) next.set(KEYS.keyword, slice.keyword);
  if (slice.sort !== DEFAULTS.sort) next.set(KEYS.sort, slice.sort);
  if (slice.order != null) next.set(KEYS.order, slice.order);
  if (slice.dateStart !== DEFAULTS.dateStart) next.set(KEYS.dateStart, slice.dateStart);
  if (slice.dateEnd !== DEFAULTS.dateEnd) next.set(KEYS.dateEnd, slice.dateEnd);
  if (slice.criticality !== DEFAULTS.criticality) next.set(KEYS.criticality, slice.criticality);
  if (slice.maintType !== DEFAULTS.maintType) next.set(KEYS.maintType, slice.maintType);
  if (slice.maintDate != null && slice.maintDate !== '') next.set(KEYS.maintDate, slice.maintDate);
  if (slice.searchType !== DEFAULTS.searchType) next.set(KEYS.searchType, slice.searchType);
  if (slice.filters != null && slice.filters.length > 0) {
    next.set(KEYS.filters, encodeURIComponent(JSON.stringify(slice.filters)));
  }
  if (slice.expandedSubmittedAssetId != null) {
    next.set(KEYS.expand, String(slice.expandedSubmittedAssetId));
  }

  return next;
}

export function assetValidationUrlSliceEquals(
  a: AssetValidationUrlSlice,
  b: AssetValidationUrlSlice
): boolean {
  return (
    a.page === b.page &&
    a.keyword === b.keyword &&
    a.sort === b.sort &&
    a.order === b.order &&
    a.dateStart === b.dateStart &&
    a.dateEnd === b.dateEnd &&
    a.criticality === b.criticality &&
    a.maintType === b.maintType &&
    a.maintDate === b.maintDate &&
    a.searchType === b.searchType &&
    JSON.stringify(a.filters ?? null) === JSON.stringify(b.filters ?? null) &&
    a.expandedSubmittedAssetId === b.expandedSubmittedAssetId
  );
}
